import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RoleEnum } from '../../../../js/api/models/RoleEnum';
import { HealthcareEntitiesService } from '../../../../js/api/services/HealthcareEntitiesService';
import { SessionContext, SessionContextType } from '../../../../js/app/contexts/SessionContext';
import { useDocumentTitle } from '../../../../js/app/hooks/use-document-title';
import { handleApiError } from '../../../../js/app/utils/errorhandling';
import { createGoogleMapsLink } from '../../../../js/utils/address';
import { Button } from '../../components/button';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../../components/dialog';
import { Heading } from '../../components/heading';
import { Navbar, NavbarItem } from '../../components/navbar';
import { useCodeDefinitionsByCategories } from '../../hooks/useCodeDefinitions';
import { DynamicFieldFactory } from '../../utils/DynamicFieldFactory';
import { AddFacilityContactDialog } from './AddFacilityContactDialog';
import { AddSpecialtyDialog } from './AddSpecialtyDialog';
import { EditFacilityContactDialog } from './EditFacilityContactDialog';
import { EditFacilityDialog } from './EditFacilityDialog';
import { FacilityContactCard } from './FacilityContactCard';
import { FacilitySpecialtiesList } from './FacilitySpecialtiesList';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const FacilityDetails: React.FC = () => {
  useDocumentTitle('Facility Details');
  const { facilityId } = useParams<{ facilityId: string }>();
  const { user } = useContext<SessionContextType>(SessionContext);
  const canEditFacility = Boolean(user?.group_names?.includes('manage_facilities'));
  const [contacts, setContacts] = useState<any[]>([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddSpecialtyDialogOpen, setIsAddSpecialtyDialogOpen] = useState(false);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [isEditContactOpen, setIsEditContactOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<any | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'specialties'>('details');

  const queryClient = useQueryClient();

  const { data: codeDefinitionsByCategory } = useCodeDefinitionsByCategories([
    'FacilityTypes',
    'HospitalOwnershipChoices',
    'EMRChoices',
    'FacilityContactRoles',
    'Specialty',
  ]);

  const enrichDynamicFieldsWithChoices = useCallback(
    (fields: any[]): any[] => {
      if (!fields || !codeDefinitionsByCategory) return [];

      return fields.map((field) => {
        if (field.choices_category) {
          const categoryDefs = codeDefinitionsByCategory[field.choices_category] || [];
          const choices = categoryDefs.map((def) => ({ code: def.code, title: def.title }));
          return { ...field, choices };
        }
        return field;
      });
    },
    [codeDefinitionsByCategory]
  );

  const { data: facilityData, isLoading: isFacilityLoading } = useQuery({
    queryKey: ['facility', facilityId],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitiesRetrieve({ id: facilityId! }),
    enabled: !!facilityId,
  });

  const { data: specialtiesData } = useQuery({
    queryKey: ['facility-specialties', facilityId],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitySpecialtiesList({
        facility: facilityId!,
      }),
    enabled: !!facilityId,
  });

  const { data: contactsData } = useQuery({
    queryKey: ['facility-contacts', facilityId],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilityContactsList({ facility: facilityId! }),
    enabled: !!facilityId,
  });

  const { data: facilityTypeFields } = useQuery({
    queryKey: ['facility-type-fields'],
    queryFn: () => HealthcareEntitiesService.healthcareEntitiesFacilityTypeFieldsList({}),
    enabled: !!facilityData,
  });

  const codeDefMaps = useMemo(() => {
    if (!codeDefinitionsByCategory) return {};
    const maps: any = {};
    Object.entries(codeDefinitionsByCategory).forEach(([category, defs]) => {
      maps[category] = {};
      defs.forEach((def) => {
        maps[category][def.code] = def.title;
      });
    });
    return maps;
  }, [codeDefinitionsByCategory]);

  const facilityTypesWithDynamicFields = useMemo(() => {
    if (!facilityTypeFields?.results) return {};
    return facilityTypeFields.results.reduce((acc: Record<string, any[]>, item) => {
      acc[item.facility_type] = item.dynamic_fields;
      return acc;
    }, {});
  }, [facilityTypeFields]);

  const facility = useMemo(() => {
    if (!facilityData || !codeDefinitionsByCategory) return null;
    return {
      ...facilityData,
      facility_type_fields: enrichDynamicFieldsWithChoices(facilityData.facility_type_fields || []),
    };
  }, [facilityData, codeDefinitionsByCategory, enrichDynamicFieldsWithChoices]);

  const specialtyOptions = useMemo(() => {
    if (!codeDefinitionsByCategory) return [];
    const specialtyDefs = codeDefinitionsByCategory.Specialty || [];
    return specialtyDefs.map((opt) => ({
      value: opt.code,
      label: opt.title,
    }));
  }, [codeDefinitionsByCategory]);

  const contactRoleOptions = useMemo(() => {
    if (!codeDefinitionsByCategory) return [];
    return codeDefinitionsByCategory.FacilityContactRoles || [];
  }, [codeDefinitionsByCategory]);

  const attachedSpecialties = useMemo(() => {
    if (!specialtiesData?.results || !codeDefinitionsByCategory) return [];
    return specialtiesData.results.map((specialty) => ({
      ...specialty,
      dynamic_fields: enrichDynamicFieldsWithChoices(
        typeof specialty.dynamic_fields === 'string'
          ? JSON.parse(specialty.dynamic_fields)
          : specialty.dynamic_fields
      ),
    }));
  }, [specialtiesData, codeDefinitionsByCategory, enrichDynamicFieldsWithChoices]);

  useEffect(() => {
    setContacts(contactsData?.results || []);
  }, [contactsData]);

  const attachedSpecialtyCodes = new Set(attachedSpecialties.map((s) => s.specialty));
  const unattachedSpecialtyOptions = specialtyOptions.filter(
    (opt) => !attachedSpecialtyCodes.has(opt.value)
  );

  const handleAddSpecialties = async (selectedSpecialtyCodes: string[]) => {
    if (!facilityId) return;
    try {
      await HealthcareEntitiesService.healthcareEntitiesFacilitySpecialtiesCreate({
        requestBody: { facility: facilityId, specialties: selectedSpecialtyCodes },
        facility: facilityId,
      });
      toast.success('Specialties added!');
      // Invalidate query to refetch specialties
      queryClient.invalidateQueries(['facility-specialties', facilityId]);
      setIsAddSpecialtyDialogOpen(false);
    } catch (err) {
      handleApiError(err, { messagePrefix: 'Failed to add specialties' });
    }
  };

  const updateContactMutation = useMutation({
    mutationFn: (data: { id: string; contact: any; role?: string }) =>
      HealthcareEntitiesService.healthcareEntitiesFacilityContactsPartialUpdate({
        id: data.id,
        requestBody: {
          contact: {
            ...data.contact,
            id: data.contact.id,
            email: data.contact.email,
          },
          role: data.role,
        },
      }),
    onSuccess: (_, variables) => {
      toast.success('Contact updated successfully!');
      setContacts((prevContacts) => {
        return prevContacts.map((contact) => {
          const isMatch = contact.id === variables.id;
          if (isMatch) {
            return {
              ...contact,
              contact: variables.contact,
              role: variables.role,
            };
          }
          return contact;
        });
      });
      setIsEditContactOpen(false);
      setEditingContact(null);
    },
    onError: (error: any) => {
      handleApiError(error, { messagePrefix: 'Failed to update contact' });
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: (id: string) =>
      HealthcareEntitiesService.healthcareEntitiesFacilityContactsDestroy({ id }),
    onSuccess: () => {
      toast.success('Contact deleted successfully!');
      setContacts((prevContacts) =>
        prevContacts.filter((contact) => contact.id !== deletingContact?.id)
      );
      setIsDeleteDialogOpen(false);
      setDeletingContact(null);
    },
    onError: (error: any) => {
      handleApiError(error, { messagePrefix: 'Failed to delete contact' });
      setIsDeleteDialogOpen(false);
      setDeletingContact(null);
    },
  });

  function toRoleEnum(val: unknown): RoleEnum | undefined {
    return typeof val === 'string' && (Object.values(RoleEnum) as string[]).includes(val)
      ? (val as RoleEnum)
      : undefined;
  }

  if (isFacilityLoading || !facility) {
    return <div className="tw:p-8 tw:text-center">Loading...</div>;
  }

  return (
    <div className="tw:mt-4 tw:max-w-4xl tw:mx-auto tw:mb-12">
      <div className="tw:bg-white tw:rounded-lg tw:shadow tw:dark:bg-zinc-700 tw:dark:border tw:dark:border-zinc-400">
        {/* Org/Facility Name Row */}
        <div className="tw:flex tw:flex-col tw:gap-1 tw:mb-4 tw:px-8 tw:pt-8">
          <h1 className="tw:!text-brand-green-0 tw:!mb-0 tw:text-2xl tw:font-bold tw:dark:!text-brand-green-1">
            {facility.name}
          </h1>
          <div className="tw:flex tw:items-center tw:text-sm tw:gap-4 tw:text-zinc-400">
            <span className="tw:flex tw:items-center tw:gap-2">
              <i className="fa-light fa-hospital" />
              <span>
                {codeDefMaps['FacilityTypes']?.[facility.facility_type] || facility.facility_type}
              </span>
            </span>
            <span className="tw:flex tw:items-center tw:gap-2">
              <i className="fa-light fa-location-dot" />
              <span>
                {facility.city ? `${facility.city}, ` : ''}{' '}
                {facility.state ? `${facility.state}` : ''}
                {!facility.city && !facility.state ? '--' : ''}
              </span>
            </span>
          </div>
        </div>
        {/* Navbar */}
        <Navbar className="tw:mb-0 tw:pl-6 tw:border-t tw:border-b tw:border-zinc-200 tw:dark:border-zinc-400">
          <NavbarItem
            type="button"
            current={activeTab === 'details'}
            onClick={() => setActiveTab('details')}
          >
            <i className="fa-solid fa-memo-circle-info tw:text-brand-green-0 tw:dark:text-brand-green-1" />
            Facility Details
          </NavbarItem>
          <NavbarItem
            type="button"
            current={activeTab === 'specialties'}
            onClick={() => setActiveTab('specialties')}
          >
            <i className="fa-regular fa-stethoscope tw:text-brand-green-0 tw:dark:text-brand-green-1" />
            Specialties
          </NavbarItem>
        </Navbar>
        {/* Tab Content */}
        {activeTab === 'details' && (
          <>
            {/* Facility Details Section */}
            <section className="tw:px-8 tw:pt-8 tw:mb-4">
              <div className="tw:mb-12">
                <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-x-8 tw:gap-y-6">
                  {/* Column 1 */}
                  <div className="tw:space-y-6">
                    {/* Address */}
                    <div className="tw:flex tw:items-start tw:justify-between">
                      {/* Address label and data */}
                      <div>
                        <div className="tw:text-zinc-400 tw:text-sm tw:flex tw:items-center tw:gap-2">
                          Address
                          {facility.street_address && (
                            <a
                              href={createGoogleMapsLink(
                                facility.street_address,
                                facility.city,
                                facility.state,
                                facility.zip_code
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              // className="tw:!text-red-500 tw:hover:!text-red-600"
                              title="Open in Google Maps"
                            >
                              <i className="fa-solid fa-location-dot" />
                            </a>
                          )}
                        </div>
                        <div className="tw:text-zinc-700 tw:dark:text-zinc-300">
                          {facility.street_address && <div>{facility.street_address}</div>}
                          {facility.street_address2 && <div>{facility.street_address2}</div>}
                          {(facility.city || facility.state || facility.zip_code) && (
                            <div>
                              {facility.city}
                              {facility.city && facility.state ? ', ' : ''}
                              {facility.state}
                              {(facility.city || facility.state) && facility.zip_code ? ' ' : ''}
                              {facility.zip_code}
                            </div>
                          )}
                          {!facility.street_address &&
                            !facility.street_address2 &&
                            !facility.city &&
                            !facility.state &&
                            !facility.zip_code && <div>--</div>}
                          <div>
                            {facility.phone_number ? (
                              <a
                                href={`tel:${facility.phone_number}`}
                                className="tw:hover:underline"
                                title={`Call ${facility.phone_number}`}
                              >
                                {facility.phone_number}
                              </a>
                            ) : (
                              '--'
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Edit button (mobile only) */}
                      {canEditFacility && (
                        <Button
                          className="tw:inline-flex tw:md:!hidden"
                          color="brand-green-outline"
                          onClick={() => setIsEditDialogOpen(true)}
                        >
                          <i className="fa-solid fa-pen-to-square" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Column 2 */}
                  <div className="tw:space-y-6 tw:relative">
                    <div className="tw:flex tw:justify-between tw:items-start">
                      <div className="tw:space-y-6">
                        <div>
                          <div className="tw:text-zinc-400 tw:text-sm">Ownership</div>
                          <div className="tw:text-zinc-700 tw:dark:text-zinc-300">
                            {codeDefMaps['HospitalOwnershipChoices']?.[
                              facility.hospital_ownership
                            ] || '--'}
                          </div>
                        </div>
                        <div>
                          <div className="tw:text-zinc-400 tw:text-sm">EMR System</div>
                          <div className="tw:text-zinc-700 tw:dark:text-zinc-300">
                            {codeDefMaps['EMRChoices']?.[facility.emr] || '--'}
                          </div>
                        </div>
                      </div>
                      {canEditFacility && (
                        <Button
                          className="tw:!hidden tw:md:!inline-flex"
                          color="brand-green-outline"
                          onClick={() => setIsEditDialogOpen(true)}
                        >
                          <i className="fa-solid fa-pen-to-square" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                {facility.extra_info && (
                  <div className="tw:mt-4">
                    <div className="tw:text-zinc-400 tw:text-sm">Additional Info</div>
                    <div className="tw:text-zinc-700 tw:dark:text-zinc-300 tw:mt-1 tw:whitespace-pre-line">
                      {facility.extra_info}
                    </div>
                  </div>
                )}
                {facility.facility_type_fields && facility.facility_type_fields.length > 0 && (
                  <div className="tw:mt-4">
                    <DynamicFieldFactory
                      fields={facility.facility_type_fields}
                      columns={2}
                      onChange={() => {}}
                      readOnly={true}
                    />
                  </div>
                )}
              </div>
            </section>
            {/* Contacts */}
            <section className="tw:px-8 tw:pb-8">
              <div className="tw:flex tw:justify-between tw:items-center tw:mb-4">
                <div className="tw:flex tw:items-center tw:gap-2">
                  <i className="fa-solid fa-user-group tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                  <Heading level={3} className="tw:!mb-0 tw:dark:!text-brand-green-1 ">
                    Contacts
                  </Heading>
                </div>
                {canEditFacility && (
                  <Button color="brand-green-outline" onClick={() => setIsAddContactOpen(true)}>
                    <i className="fa-solid fa-plus" />
                    Add Contact
                  </Button>
                )}
              </div>
              <div className="tw:grid tw:grid-cols-1 tw:gap-4">
                {contacts.length > 0 ? (
                  contacts.map((facilityContact) => (
                    <FacilityContactCard
                      key={facilityContact.id}
                      facilityContact={facilityContact}
                      onEdit={
                        canEditFacility
                          ? (fc) => {
                              setEditingContact(fc);
                              setIsEditContactOpen(true);
                            }
                          : undefined
                      }
                      onDelete={
                        canEditFacility
                          ? (fc) => {
                              setDeletingContact(fc);
                              setIsDeleteDialogOpen(true);
                            }
                          : undefined
                      }
                      canEdit={canEditFacility}
                      roleMap={codeDefMaps['FacilityContactRoles']}
                    />
                  ))
                ) : (
                  <div className="tw:text-zinc-500 tw:dark:text-zinc-400">No contacts yet.</div>
                )}
              </div>
            </section>
          </>
        )}
        {activeTab === 'specialties' && (
          <>
            {/* Specialties */}
            <section className="tw:px-8 tw:pt-8 tw:pb-8">
              <div className="tw:flex tw:justify-between tw:items-center tw:mb-4">
                <Heading level={3} className="tw:!mb-0 tw:dark:!text-brand-green-1">
                  Specialty Support
                </Heading>
                {canEditFacility && (
                  <Button
                    color="brand-green-outline"
                    onClick={() => setIsAddSpecialtyDialogOpen(true)}
                  >
                    <i className="fa-solid fa-plus" />
                    Add Specialty
                  </Button>
                )}
              </div>
              <FacilitySpecialtiesList
                facilityId={facilityId!}
                specialtyOptions={specialtyOptions}
                specialties={attachedSpecialties}
                onUpdate={() => queryClient.invalidateQueries(['facility-specialties', facilityId])}
                canEdit={canEditFacility}
              />
            </section>
          </>
        )}
      </div>

      <EditFacilityDialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        facilityId={facilityId!}
        initialData={facility}
        onSuccess={() => {
          setIsEditDialogOpen(false);
          queryClient.invalidateQueries(['facility', facilityId]);
        }}
        facilityTypesWithDynamicFields={facilityTypesWithDynamicFields}
      />

      <AddSpecialtyDialog
        open={isAddSpecialtyDialogOpen}
        onClose={() => setIsAddSpecialtyDialogOpen(false)}
        specialtyOptions={unattachedSpecialtyOptions}
        onSave={handleAddSpecialties}
      />

      {/* Add Facility Contact Dialog */}
      <AddFacilityContactDialog
        facilityId={facilityId!}
        open={isAddContactOpen}
        onClose={() => setIsAddContactOpen(false)}
        roleOptions={contactRoleOptions}
        onSuccess={(newContacts) => {
          setContacts((prevContacts) => [...prevContacts, ...newContacts]);
          setIsAddContactOpen(false);
        }}
      />

      {/* Edit Facility Contact Dialog */}
      <EditFacilityContactDialog
        open={isEditContactOpen}
        onClose={() => {
          setIsEditContactOpen(false);
          setEditingContact(null);
        }}
        onSave={(updated) => {
          if (!editingContact) return;
          updateContactMutation.mutate({
            id: editingContact.id,
            contact: {
              ...editingContact.contact,
              ...updated,
              id: editingContact.contact.id,
              email: editingContact.contact.email,
            },
            role: updated.role,
          });
        }}
        roleOptions={contactRoleOptions}
        contact={
          editingContact
            ? {
                ...editingContact.contact,
                role: toRoleEnum(editingContact.role),
              }
            : undefined
        }
      />

      {/* Delete Facility Contact Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        className="tw:p-6"
      >
        <DialogTitle className="tw:dark:!text-white/90">Remove Facility Contact</DialogTitle>
        <DialogBody>
          <p className="tw:text-zinc-900 tw:dark:text-zinc-100">
            Are you sure you want to remove this contact?
          </p>
          <div className="tw:text-zinc-900 tw:dark:text-zinc-100">
            {deletingContact?.contact?.first_name} {deletingContact?.contact?.last_name}
            {deletingContact?.role && (
              <div className="tw-text-xs tw:text-zinc-500">
                {codeDefMaps['FacilityContactRoles']?.[deletingContact.role] ||
                  deletingContact.role}
              </div>
            )}
            {deletingContact?.contact?.email && (
              <div className="tw-text-xs tw:text-zinc-500">{deletingContact.contact.email}</div>
            )}
          </div>
        </DialogBody>
        <DialogActions>
          <Button type="button" plain onClick={() => setIsDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            plain
            className="tw:bg-stoplight-danger tw:!text-white tw:hover:!bg-stoplight-danger/90"
            onClick={() => {
              if (deletingContact) {
                deleteContactMutation.mutate(deletingContact.id);
              }
            }}
            disabled={deleteContactMutation.isLoading}
          >
            {deleteContactMutation.isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
